import { type FC, useEffect, useState } from 'react'
import { Form } from 'react-bootstrap'
import { NavLink } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { skipToken } from '@reduxjs/toolkit/query'
import classNames from 'classnames'
import { Button } from '../../../../components/Button/Button'
import { type AccountSchedulingSettings, type ConsentSettings } from '../../../../models/Scheduling'
import { useAppSelector } from '../../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../../store/auth/authSlice'
import { useConsentManualConfigs } from '../../../../components/Consent/useConsentManualConfigs'
import { useGetAccountQuery } from '../../../../store/accountUserSettings/accountUserSettings'
import { useDownloadGlobalFileRequestQuery } from '../../../../store/media/file'
import ZocksLogoFullBlack from '../../../../assets/images/ZocksLogoFullBlack.png'
import { ReactComponent as ChevronBullet } from '../../../../assets/images/chevron-bullet.svg'
import { InfoTooltip } from '../../../Scheduling/shared/InfoTooltip'
import { BOOKING_BASE_URL } from '../../../Scheduling/shared/bookingBaseUrl'
import { BrandColorSelect } from '../../../Scheduling/shared/BrandColorSelect'
import { PreviewLabel } from '../../../Scheduling/shared/PreviewLabel'
import { type UpdateAccountSettings } from '../SchedulingSetupTab'
import { SettingsCard, SettingsRow, SettingsSection, SettingsToggle } from './SettingsSection'
import styles from './SchedulingSettings.module.scss'

interface SectionProps {
  settings: AccountSchedulingSettings
  readOnly: boolean
  isSaving?: boolean
  onUpdate: UpdateAccountSettings
}

const DEFAULT_CONSENT_CHECKBOX_TEXT = 'I consent to Zocks transcribing and analyzing this meeting with AI to create notes.'

const MAX_CONSENT_TEXT_LENGTH = 500

const ConsentMessageEditor: FC<{
  savedText: string | null | undefined
  readOnly: boolean
  onSave: (text: string | null) => void
}> = ({ savedText, readOnly, onSave }) => {
  const savedEffective = (savedText ?? '').trim() || DEFAULT_CONSENT_CHECKBOX_TEXT
  const [draft, setDraft] = useState(savedEffective)
  useEffect(() => {
    setDraft(savedEffective)
  }, [savedEffective])

  if (readOnly) {
    return <div className='paragraph-medium zocks-text-gray-700 zocks-px-0.75 py-2 rounded-3 zocks-bg-color-gray-100'>{savedEffective}</div>
  }

  const remainingCharacters = MAX_CONSENT_TEXT_LENGTH - draft.length
  const dirty = draft.trim() !== savedEffective
  const isAtDefault = draft.trim() === DEFAULT_CONSENT_CHECKBOX_TEXT
  const saveDraft = () => {
    const cleaned = draft.trim()
    onSave(!cleaned || cleaned === DEFAULT_CONSENT_CHECKBOX_TEXT ? null : cleaned)
  }

  return (
    <div className='d-flex flex-column zocks-gap-0.5'>
      <div className='d-flex flex-column zocks-gap-0.25'>
        <label className='paragraph-small zocks-text-gray-900' htmlFor='scheduling-consent-message'>
          Message
        </label>
        <Form.Control
          as='textarea'
          id='scheduling-consent-message'
          rows={3}
          value={draft}
          maxLength={MAX_CONSENT_TEXT_LENGTH}
          onChange={(event) => setDraft(event.currentTarget.value)}
        />
        <div
          className={classNames('paragraph-extra-small text-end', {
            'zocks-text-gray-600': remainingCharacters > 0,
            'zocks-text-danger-200': remainingCharacters === 0,
          })}
        >
          {remainingCharacters} characters left
        </div>
      </div>
      <div className='d-flex align-items-center justify-content-between gap-3'>
        <Button
          variant='link'
          className={classNames('p-1 button-small fw-bold text-decoration-none', isAtDefault ? 'zocks-text-gray-500' : 'zocks-text-primary')}
          disabled={isAtDefault}
          onClick={() => setDraft(DEFAULT_CONSENT_CHECKBOX_TEXT)}
        >
          Reset to default
        </Button>
        <div className='d-flex align-items-center zocks-gap-0.5'>
          <Button variant='grayDarkGhostButton' onClick={() => setDraft(savedEffective)} className='zocks-py-0.25 zocks-px-0.75'>
            Cancel
          </Button>
          <Button variant='primary' disabled={!dirty} onClick={saveDraft} className='zocks-py-0.25 zocks-px-0.75'>
            Save changes
          </Button>
        </div>
      </div>
    </div>
  )
}

const DisclosureTextControl: FC<{
  value: string
  readOnly: boolean
  onSave: (value: string) => void
}> = ({ value, readOnly, onSave }) => {
  const [text, setText] = useState(value)
  if (readOnly) {
    return <div className='paragraph-medium zocks-text-gray-700 zocks-px-0.75 py-2 rounded-3 zocks-bg-color-gray-100'>{value}</div>
  }
  return (
    <Form.Control
      as='textarea'
      rows={3}
      value={text}
      placeholder='For example: This meeting may be noted for compliance purposes.'
      aria-label='Disclosure text'
      onChange={(event) => setText(event.currentTarget.value)}
      onBlur={() => {
        if (text !== value) {
          onSave(text)
        }
      }}
    />
  )
}

export const PublicBookingPageSection: FC<SectionProps> = ({ settings, readOnly, isSaving = false, onUpdate }) => {
  const accountId = useAppSelector(selectSafeAuthenticatedUser).accountId
  const { data: account } = useGetAccountQuery({ accountId })
  const logoFileId = account?.data.effectiveConfig.assets?.logo?.logoFile?.fileId
  const { data: logoDownload } = useDownloadGlobalFileRequestQuery(logoFileId ? { accountId, fileId: logoFileId } : skipToken)
  const logoUrl = logoFileId ? logoDownload?.data.location : ZocksLogoFullBlack
  const publicBookingPage = settings.publicBookingPage
  const accountSlug = publicBookingPage.accountSlug
  const firmName = account?.data.name ?? ''

  useEffect(() => {
    if (!accountSlug) {
      Sentry.captureException(new Error('Scheduling: account is missing accountSlug; cannot build a booking URL in the public booking page section'))
    }
  }, [accountSlug])

  const showLogo = publicBookingPage.showLogo
  const compliance = settings.compliance
  const hasDisclosureText = Boolean(compliance.disclosureText)
  const [disclosureDraftOpen, setDisclosureDraftOpen] = useState(false)
  const disclosureEnabled = hasDisclosureText || disclosureDraftOpen

  const { userConsentTrackingEnabled } = useConsentManualConfigs()
  const consent: ConsentSettings = compliance.consent ?? { enabled: false }
  const consentLocked = !userConsentTrackingEnabled
  const updateConsent = (patch: Partial<ConsentSettings>) => onUpdate({ compliance: { ...compliance, consent: { ...consent, ...patch } } })

  return (
    <SettingsSection title='Public booking page' subtitle='Where clients book meetings with you.' isSaving={isSaving}>
      <SettingsCard>
        <SettingsRow className='d-flex flex-wrap align-items-start justify-content-between gap-3'>
          <div className='d-flex flex-column'>
            <span className='bold'>Account booking URL</span>
            <span className='paragraph-small zocks-text-gray-600'>The account name in each user&apos;s booking link.</span>
          </div>
          {accountSlug ? (
            <div className='d-flex align-items-center zocks-gap-0.5'>
              <span className='paragraph-small zocks-text-gray-600'>
                {`${BOOKING_BASE_URL}/`}
                <span className='bold zocks-text-gray-900'>{accountSlug}</span>/user-name
              </span>
              <InfoTooltip title='To customize booking URLs, reach out to your implementation manager at Zocks.' label='About the account booking URL' />
            </div>
          ) : (
            <span className='paragraph-small zocks-text-danger'>Booking URL unavailable</span>
          )}
        </SettingsRow>
      </SettingsCard>

      <h6 className='mb-0 zocks-text-gray-900'>Brand</h6>
      <SettingsCard>
        <SettingsRow className='d-flex align-items-center justify-content-between gap-3'>
          <div className='d-flex flex-column'>
            <span className='bold'>Brand color</span>
            <span className='paragraph-small zocks-text-gray-600'>Used for buttons and highlights on the booking page. Defaults to Zocks purple.</span>
          </div>
          <BrandColorSelect
            value={publicBookingPage.branding.brandColor}
            readOnly={readOnly}
            onChange={(brandColor) => onUpdate({ publicBookingPage: { ...publicBookingPage, branding: { ...publicBookingPage.branding, brandColor } } })}
          />
        </SettingsRow>
        <SettingsRow className='d-flex flex-column zocks-gap-0.75'>
          <SettingsToggle
            label='Logo'
            description={
              <>
                Show logo at the top of the booking page. Upload in{' '}
                <NavLink className='zocks-text-primary text-decoration-underline' to='/workspace/setup/settings/account'>
                  Account Settings
                </NavLink>
                .
              </>
            }
            checked={showLogo}
            readOnly={readOnly}
            ariaLabel='Show logo'
            onChange={(checked) => onUpdate({ publicBookingPage: { ...publicBookingPage, showLogo: checked } })}
          />
          {showLogo ? (
            <div className='d-flex align-items-center justify-content-between gap-3 ps-3 pe-1 py-1 rounded-3 zocks-bg-color-gray-100'>
              <PreviewLabel />
              {logoUrl ? (
                <img src={logoUrl} alt='Account logo' className={classNames(styles.logoPreview, 'rounded-1 flex-shrink-0 object-fit-contain')} />
              ) : (
                <span className={`${styles.logoPreview} d-inline-block rounded-1 zocks-bg-color-gray-200 flex-shrink-0`} />
              )}
            </div>
          ) : null}
        </SettingsRow>
        <SettingsRow className='d-flex flex-column zocks-gap-0.75'>
          <SettingsToggle
            label='Firm name'
            description={
              <>
                Show your account name to clients on the booking page. Edit it in{' '}
                <NavLink className='zocks-text-primary text-decoration-underline' to='/workspace/setup/settings/account'>
                  Account Settings
                </NavLink>
                .
              </>
            }
            checked={publicBookingPage.showAccountName}
            readOnly={readOnly}
            ariaLabel='Show firm name'
            onChange={(checked) => onUpdate({ publicBookingPage: { ...publicBookingPage, showAccountName: checked } })}
          />
          {publicBookingPage.showAccountName && firmName ? (
            <div className='d-flex flex-column zocks-gap-0.25 px-3 py-2 rounded-3 zocks-bg-color-gray-100'>
              <PreviewLabel showStatus={false} />
              <span className='paragraph-small zocks-text-gray-900 ps-4'>{firmName}</span>
            </div>
          ) : null}
          <div className='d-flex align-items-center zocks-gap-0.5'>
            <i className='bi bi-info-circle zocks-text-gray-700 flex-shrink-0' aria-hidden='true' />
            <span className='paragraph-small zocks-text-gray-700'>Firm name always appears in emails sent to clients.</span>
          </div>
        </SettingsRow>
        <SettingsRow>
          <SettingsToggle
            label='Profile photos'
            description="Let users show their profile photo on the booking page. Each user still chooses their own in their profile, so turning this on doesn't reveal any photo by itself."
            checked={publicBookingPage.showProfilePhotos}
            readOnly={readOnly}
            ariaLabel='Allow profile photos'
            onChange={(showProfilePhotos) => onUpdate({ publicBookingPage: { ...publicBookingPage, showProfilePhotos } })}
          />
        </SettingsRow>
      </SettingsCard>

      <h6 className='mb-0 zocks-text-gray-900'>Compliance</h6>
      <SettingsCard>
        <SettingsRow className='d-flex flex-column zocks-gap-0.5'>
          <SettingsToggle
            label='Disclosure text'
            description='Show a message on the booking page before clients confirm. Use it for compliance disclosures.'
            checked={disclosureEnabled}
            readOnly={readOnly}
            ariaLabel='Enable disclosure text'
            onChange={(checked) => {
              setDisclosureDraftOpen(checked)
              if (!checked && hasDisclosureText) {
                onUpdate({ compliance: { ...compliance, disclosureText: '' } })
              }
            }}
          />
          {disclosureEnabled ? (
            <DisclosureTextControl
              value={compliance.disclosureText}
              readOnly={readOnly}
              onSave={(disclosureText) => onUpdate({ compliance: { ...compliance, disclosureText } })}
            />
          ) : null}
        </SettingsRow>
        <SettingsRow className={classNames('d-flex flex-column zocks-gap-0.75', consentLocked ? 'zocks-bg-color-gray-100' : undefined)}>
          <SettingsToggle
            label='Show consent checkbox'
            description='Ask clients to accept a consent message when they book.'
            checked={consent.enabled && !consentLocked}
            readOnly={readOnly || consentLocked}
            ariaLabel='Show consent checkbox'
            onChange={(enabled) => updateConsent({ enabled })}
          />
          <div
            className={classNames(
              'd-flex align-items-start zocks-gap-0.5 zocks-px-0.75 py-3 rounded-3',
              consentLocked ? 'zocks-bg-color-gray-200' : 'zocks-bg-color-gray-100',
            )}
          >
            <i className='bi bi-info-circle zocks-text-gray-700 flex-shrink-0' aria-hidden='true' />
            <span className='paragraph-small zocks-text-gray-700'>
              {consentLocked
                ? 'Consent lets your AI Assistant transcribe and analyze meetings. Turn on client consent tracking to enable this. '
                : "Consent lets your AI Assistant transcribe and analyze meetings. Answers given at booking are saved to the client's consent history. "}
              <NavLink to='/workspace/setup/settings/compliance' className='zocks-text-primary text-decoration-underline'>
                Manage consent tracking
              </NavLink>
            </span>
          </div>
        </SettingsRow>
        {consent.enabled && !consentLocked ? (
          <>
            <SettingsRow className='d-flex flex-column'>
              <div className='d-flex align-items-center zocks-gap-1'>
                <ChevronBullet className='flex-shrink-0' aria-hidden='true' />
                <span className='bold label-small'>Consent message</span>
              </div>
              <div className='d-flex flex-column zocks-gap-0.75 zocks-pl-2.0'>
                <span className='paragraph-small zocks-text-gray-600'>Shown next to the checkbox when clients book. Edit it or keep the default text.</span>
                <ConsentMessageEditor savedText={consent.text} readOnly={readOnly} onSave={(text) => updateConsent({ text })} />
              </div>
            </SettingsRow>
          </>
        ) : null}
      </SettingsCard>
    </SettingsSection>
  )
}
